import { useEffect, useRef, useState } from 'react';

const RADIUS = 90;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

function colorForPercentage(percentage: number): string {
  if (percentage < 40) return 'var(--color-coral)';
  if (percentage < 61) return 'var(--color-gold)';
  if (percentage < 83) return 'var(--color-notice)';
  return 'var(--color-teal)';
}

interface ScoreCircleProps {
  score: number;
  maximum: number;
  percentage: number;
  statusLabel: string;
}

/** Eigener SVG-Score-Kreis ohne Chart-Bibliothek (Abschnitt 15.3, AC-007). */
export function ScoreCircle({ score, maximum, percentage, statusLabel }: ScoreCircleProps) {
  const [animatedPercentage, setAnimatedPercentage] = useState(() =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ? percentage : 0,
  );
  const frameRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const durationMs = 1100;
    const start = performance.now();

    function tick(now: number): void {
      const elapsed = now - start;
      const progress = Math.min(1, elapsed / durationMs);
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimatedPercentage(percentage * eased);
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick);
      }
    }

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [percentage]);

  const offset = CIRCUMFERENCE * (1 - animatedPercentage / 100);
  const displayedScore = Math.round((animatedPercentage / 100) * maximum);
  const description = `Gesamt-Ergebnis: ${score} von ${maximum} Punkten, ${percentage} Prozent, ${statusLabel}.`;

  return (
    <svg className="score-circle" viewBox="0 0 200 200" role="img" aria-label={description}>
      <circle className="score-circle__track" cx="100" cy="100" r={RADIUS} />
      <circle
        className="score-circle__value"
        cx="100"
        cy="100"
        r={RADIUS}
        stroke={colorForPercentage(percentage)}
        strokeDasharray={CIRCUMFERENCE}
        strokeDashoffset={offset}
      />
      <text
        x="100"
        y="92"
        textAnchor="middle"
        className="score-circle__label-score"
        fill="currentColor"
      >
        {displayedScore} / {maximum}
      </text>
      <text
        x="100"
        y="118"
        textAnchor="middle"
        className="score-circle__label-percentage"
        fill="currentColor"
      >
        {Math.round(animatedPercentage)} %
      </text>
    </svg>
  );
}
